import { Fragment } from 'react'

import Title from '@/components/title/Title'
import Text from '@/components/text/Text'
import Columns from '@/components/columns/Columns'
import Blockquote from '@/components/blockquote/Blockquote'
import CustomImage from '@/components/image/Image'
import Gallery from '@/components/gallery/Gallery'
import ImageAndText from '@/components/image-and-text/ImageAndText'
import Video from '@/components/video/Video'
import VideoAndText from '@/components/video/VideoAndText'
import VideoGrid from '@/components/video/VideoGrid'
import LinkGrid from '@/components/link-grid/LinkGrid'
import ListGrid from '@/components/list-grid/ListGrid'
import LatestPosts from '@/components/latest-posts/LatestPosts'
import DownloadsAndLinks from '@/components/downloads-and-links/DownloadsAndLinks'
import PromoBlock from '@/components/promo-block/PromoBlock'
import FeaturedPost from '@/components/featured-post/FeaturedPost'
import Accordion from '@/components/accordion/Accordion'
import AnchoredContent from '@/components/anchored-content/AnchoredContent'
import Agenda from '@/components/agenda/Agenda'
import StatsGrid from '@/components/stats/StatsGrid'
import LogoGrid from '@/components/logo-grid/LogoGrid'
import LogoCarousel from '@/components/logo-carousel/LogoCarousel'
import PageAnchors from '@/components/page-anchors/PageAnchors'
import GoogleMap from '@/components/google-map/GoogleMap'
import People from '@/components/people-grid/PeopleGrid'
import SectorSlider from '@/components/sector-slider/SectorSlider'
import { slugify } from '@/lib/sanitise-strings'
import CustomHtml from '@/components/custom-html/CustomHtml'

export type RenderComponentsProps = {
  components: any[]
}

/**
 * --------------------------------------------------------------------------
 * RenderComponents
 *
 * Render a list of components based on their type
 *
 * @param {Object} components - List of components to render
 * --------------------------------------------------------------------------
 **/
export default function RenderComponents({ components }: RenderComponentsProps) {
  if (!components || components.length === 0) {
    return null
  }

  let downloadIndex = 1

  return Object.values(components).map((component: any, index: number) => {
    const { acf_fc_layout: componentName } = component

    const key = componentName + index
    let componentOutput

    switch (componentName) {
      case 'title':
        componentOutput = (
          <Title title={component?.title} titleTag={component?.tag} titleClass={component?.class} />
        )
        break

      case 'text':
        componentOutput = (
          <Text
            twoColumn={component?.two_column}
            heading={component?.heading}
            title={component?.title}
            firstColumnText={component?.first_column_text}
            text={component?.text}
          />
        )
        break

      case 'columns':
        componentOutput = <Columns columns={component?.columns} />
        break

      case 'blockquote':
        componentOutput = (
          <Blockquote
            quote={component?.quote}
            extraText={component?.extra_text}
            personName={component?.person?.name}
            personTitle={component?.person?.job_title}
            personImage={component?.person?.image}
            personLink={component?.person?.link}
          />
        )
        break

      case 'image':
        componentOutput = (
          <CustomImage image={component?.image} reduceSize={component?.reduce_width} lazy={true} />
        )
        break

      case 'gallery':
        componentOutput = <Gallery images={component?.images} />
        break

      case 'image_and_text':
        componentOutput = (
          <ImageAndText
            images={component?.images?.images}
            heading={component.content?.heading}
            title={component?.content?.title?.text}
            titleTag={component?.content?.title?.tag}
            titleClass={component?.content?.title?.class}
            text={component?.content?.text}
            button={component?.content?.button}
          />
        )
        break

      case 'promo_block':
        componentOutput = (
          <PromoBlock
            heading={component?.promo_content?.heading}
            title={component?.promo_content?.title}
            text={component?.promo_content?.text}
            link={component?.link}
          />
        )
        break

      case 'full_width_banner':
        componentOutput = (
          <PromoBlock
            heading={component?.heading}
            title={component?.title}
            text={component?.text}
            link={component?.link}
          />
        )
        break

      case 'featured_post':
        // First item from relationship field, or legacy post object ID
        const featuredPostId = component?.post[0] || component?.post

        componentOutput = (
          <FeaturedPost
            postId={featuredPostId}
            customLinkText={component?.custom_link_text || 'Read more'}
          />
        )
        break

      case 'video_and_text':
        componentOutput = (
          <VideoAndText
            video={component?.video}
            heading={component?.content?.heading}
            title={component?.content?.title?.text}
            titleTag={component?.content?.title?.tag}
            titleClass={component?.content?.title?.class}
            text={component?.content?.text}
            button={component?.content?.button}
          />
        )
        break

      case 'video':
        componentOutput = <Video video={component?.video} />
        break

      case 'video_grid':
        componentOutput = <VideoGrid videos={component.videos} />
        break

      case 'link_grid':
        componentOutput = <LinkGrid links={component?.links} columns={component?.columns} />
        break

      case 'list_grid':
        componentOutput = <ListGrid lists={component?.lists} />
        break

      case 'latest_posts':
        componentOutput = (
          <LatestPosts
            style={component?.style}
            title={component?.title?.text}
            titleTag={component?.title?.tag}
            titleClass={component?.title?.class}
            text={component?.intro}
            button={component?.link}
            fallbackText={component?.empty}
            postType={component?.post_type}
            postIds={component?.posts}
            topic={component?.topic}
            market={component?.market}
            solution={component?.solution}
            author={component?.author}
            type={component?.type}
          />
        )
        break

      case 'downloads':
        componentOutput = (
          <DownloadsAndLinks items={component?.download_list} anchorIndex={downloadIndex++} />
        )
        break

      // TODO: Key contact
      // TODO: Opportunities

      case 'people':
        componentOutput = <People people={component?.people} slider={component?.enable_slider} />
        break

      case 'accordion':
        componentOutput = <Accordion items={component?.accordion} />
        break

      case 'faqs':
        componentOutput = <Accordion items={component?.faqs} />
        break

      case 'icon_grid':
        componentOutput = (
          <StatsGrid
            items={component?.items}
            columns={component?.columns}
            heading={component.content?.heading}
            title={component?.content?.title?.text}
            titleTag={component?.content?.title?.tag}
            titleClass={component?.content?.title?.class}
            text={component?.content?.text}
            button={component?.content?.button}
          />
        )
        break

      case 'logo_grid':
        componentOutput = (
          <LogoGrid
            items={component?.logos}
            columns={component?.columns}
            heading={component.content?.heading}
            title={component?.content?.title?.text}
            titleTag={component?.content?.title?.tag}
            titleClass={component?.content?.title?.class}
            text={component?.content?.text}
            button={component?.content?.button}
          />
        )
        break

      case 'logo_carousel':
        componentOutput = (
          <div className="container m-up:px-xl l-up:px-xxl">
            <LogoCarousel heading={component.carousel_heading} logos={component.carousel_logos} />
          </div>
        )
        break

      case 'statistics':
        const statsItems = component?.stats
          ? component.stats.map((item) => {
              return {
                icon: item.icon,
                title: item.value,
                text: item.text,
              }
            })
          : []

        componentOutput = (
          <StatsGrid
            items={statsItems}
            columns={5}
            heading={component.content?.heading}
            title={component?.content?.title?.text}
            titleTag={component?.content?.title?.tag}
            titleClass={component?.content?.title?.class}
            text={component?.content?.text}
            button={component?.content?.button}
          />
        )
        break

      case 'custom_script':
        componentOutput = <CustomHtml html={component?.code || ''} />
        break

      case 'anchored_content':
        if (!component?.sections) {
          return null
        }

        const anchoredContentSections = component.sections.map((section) => {
          const anchorId = (section.anchorId = section.anchorId || slugify(section.title))
          return {
            anchorId: anchorId,
            title: section.title,
            content: (
              <section className={`bg-theme p-[var(--container-padding)] text-fg`} key={anchorId}>
                <h3 className="mb-0">{section.title}</h3>
                <RenderComponents components={section.anchored_content} />
              </section>
            ),
          }
        })

        componentOutput = <AnchoredContent sections={anchoredContentSections} />
        break

      case 'sector_slider':
        componentOutput = <SectorSlider key={key} title={component?.title} text={component?.text} />
        break

      case 'page_anchors':
        componentOutput = (
          <PageAnchors
            key={key}
            label={component?.label}
            anchors={component?.anchors}
            style={component?.style}
          />
        )
        break

      case 'anchor':
        componentOutput = component?.anchor_id ? <div id={component?.anchor_id}></div> : null
        break

      case 'agenda':
        componentOutput = <Agenda {...component} />
        break

      case 'google_map':
        const { address: mapAddress, lat: mapLat, lng: mapLng } = component?.google_map

        componentOutput = <GoogleMap address={mapAddress} lat={mapLat} lng={mapLng} />

        break

      default:
        break
    }

    return <Fragment key={key}>{componentOutput}</Fragment>
  })
}
